import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WebClient } from '@injectables';
import { Device } from '@models';
import { firstValueFrom } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { percentageNewColors } from 'src/colors';
import { parsePsdControlLines, PsdControlLine, supportedPsdControlLinePercents } from '../../../../../../shared-utils/psd-control-lines';
import { isFiniteNumber } from '@lib/number';

@Component({
  selector: 'dialog-psd-control-lines',
  templateUrl: './dialog-psd-control-lines.component.html',
  styleUrls: ['./dialog-psd-control-lines.component.scss'],
})
export class DialogPsdControlLinesComponent implements OnInit {
  public device: Device;
  public controlLines: PsdControlLine[] = [];
  public originalControlLines: PsdControlLine[] = [];
  public newControlLine: PsdControlLine = { percent: 80, value: null };
  public changesMade = false;
  public saving = false;
  public loadError = '';

  public readonly percents: number[] = [...supportedPsdControlLinePercents];
  public readonly colors = percentageNewColors;
  public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;
  public readonly restClient = this.webClient.model.device;

  constructor(
    public readonly fragQuery: FragQueryService,
    public readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly webClient: WebClient,
  ) {}

  public ngOnInit(): void {
    void this.loadDeviceControls();
  }

  private async loadDeviceControls(): Promise<void> {
    this.device = await firstValueFrom(this.fragQuery.device$);
    if (!this.device) {
      this.loadError = 'No device is selected.';
      return;
    }

    try {
      this.controlLines = parsePsdControlLines(this.device.psdControlLines);
      this.originalControlLines = this.cloneControls(this.controlLines);
      this.selectNextPercent();
    } catch {
      this.loadError = 'The saved PSD control-line settings are invalid. Please contact an administrator.';
    }
  }

  public addControlLine(): void {
    if (!this.isValidControl(this.newControlLine) || !this.isPercentAvailable(this.newControlLine.percent)) {
      return;
    }

    this.controlLines = [...this.controlLines, { ...this.newControlLine }]
      .sort((a, b) => a.percent - b.percent);
    this.newControlLine = { percent: 80, value: null };
    this.selectNextPercent();
    this.checkForChanges();
  }

  public removeControlLine(index: number): void {
    this.controlLines = this.controlLines.filter((_, controlIndex) => controlIndex !== index);
    this.selectNextPercent();
    this.checkForChanges();
  }

  public isPercentAvailable(percent: number): boolean {
    return !this.controlLines.some(control => control.percent === percent);
  }

  public canAddControlLine(): boolean {
    return !this.saving && this.isValidControl(this.newControlLine) && this.isPercentAvailable(this.newControlLine.percent);
  }

  public canSave(): boolean {
    return this.changesMade
      && !this.saving
      && !this.loadError
      && this.controlLines.every(control => this.isValidControl(control));
  }

  public checkForChanges(): void {
    this.controlLines.sort((a, b) => a.percent - b.percent);
    this.changesMade = JSON.stringify(this.controlLines) !== JSON.stringify(this.originalControlLines);
  }

  public cancel(): void {
    this.dialog.closeAll();
  }

  public async save(): Promise<void> {
    if (!this.canSave()) {
      return;
    }

    this.saving = true;
    const previousValue = this.device.psdControlLines;
    try {
      this.controlLines.sort((a, b) => a.percent - b.percent);
      const serialized = JSON.stringify(this.controlLines);
      parsePsdControlLines(serialized);
      this.device.psdControlLines = serialized;

      const saved = await this.restClient.save(this.device);
      if (!saved) {
        throw new Error('PSD control-line settings were not saved.');
      }

      this.originalControlLines = this.cloneControls(this.controlLines);
      this.changesMade = false;
      this.snackBar.open('PSD control lines saved successfully', 'OK', {
        duration: 5000,
        panelClass: ['snackbar-success'],
      });
      this.dialog.closeAll();
      this.fragQuery.trigger$.next();
    
    } catch (error) {
      this.device.psdControlLines = previousValue;
      this.snackBar.open('Could not save PSD control lines', 'OK', {
        duration: 5000,
      });
      console.error('Error saving PSD control lines:', error);
    } finally {
      this.saving = false;
    }
  }

  private isValidControl(control: PsdControlLine): boolean {
    return this.percents.includes(control.percent)
      && isFiniteNumber(control.value)
      && control.value > 0;
  }

  private selectNextPercent(): void {
    const nextPercent = this.percents.find(percent => this.isPercentAvailable(percent));
    this.newControlLine.percent = nextPercent ?? 80;
  }

  private cloneControls(controls: PsdControlLine[]): PsdControlLine[] {
    return controls.map(control => ({ ...control }));
  }
}
